import { Link, useParams } from 'react-router-dom';
import {
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  LightBulbIcon,
  ClockIcon,
  BoltIcon
} from '@heroicons/react/24/outline';
import Navbar from '../../components/Navbar';
import Chatbot from '../../components/Chatbot';

export default function IndexedQueriesPage() {
  const { orgId } = useParams();
  const basePath = orgId ? `/${orgId}` : '';

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link to={`${basePath}/help`} className="text-blue-600 hover:text-blue-800 text-sm font-medium">
            ← Back to Help Center
          </Link>
          <h1 className="text-3xl font-bold text-gray-900 mt-4">Query Performance & Indexes</h1>
          <p className="text-lg text-gray-600 mt-2">
            Understanding how to write fast, efficient Flow queries using indexed fields
          </p>
        </div>

        {/* Important Alert */}
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-8">
          <div className="flex">
            <ClockIcon className="h-6 w-6 text-yellow-400 mr-3 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-yellow-800">Performance Impact</h3>
              <p className="mt-2 text-sm text-yellow-700">
                Queries on non-indexed fields can be 10-100x slower than indexed queries, especially
                in orgs with large data volumes. This can lead to timeouts and poor performance.
              </p>
            </div>
          </div>
        </div>

        {/* What Are Indexes? */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">What Are Database Indexes?</h2>

          <p className="text-gray-700 mb-4">
            A database index is like a book's index - it helps Salesforce quickly find records without
            scanning the entire table. Think of it this way:
          </p>

          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div className="bg-red-50 border border-red-200 p-4 rounded">
              <h3 className="font-semibold text-red-800 mb-2">❌ Without Index</h3>
              <p className="text-sm text-red-700">
                Salesforce must scan every single record in the table to find matches. With 1 million
                Accounts, that means checking all 1 million records.
              </p>
              <div className="mt-2 text-xs text-red-600 font-mono">
                Query time: 10-30 seconds
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 p-4 rounded">
              <h3 className="font-semibold text-green-800 mb-2">✅ With Index</h3>
              <p className="text-sm text-green-700">
                Salesforce uses the index to jump directly to matching records. With 1 million
                Accounts, it might only need to check 100 records.
              </p>
              <div className="mt-2 text-xs text-green-600 font-mono">
                Query time: 0.1-0.5 seconds
              </div>
            </div>
          </div>

          <div className="bg-blue-50 p-4 rounded">
            <p className="text-sm text-blue-800">
              <strong>The Impact:</strong> Indexed queries can be 10-100x faster. In flows processing
              hundreds of records, this difference can mean the difference between success and timeout.
            </p>
          </div>
        </div>

        {/* Which Fields Are Indexed? */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Which Fields Are Indexed by Default?</h2>

          <p className="text-gray-700 mb-4">
            Salesforce automatically indexes certain types of fields:
          </p>

          <div className="space-y-4">
            <div className="border-l-4 border-green-500 pl-4">
              <h3 className="font-semibold text-green-800">Always Indexed:</h3>
              <ul className="text-gray-700 text-sm space-y-1 list-disc list-inside ml-2 mt-2">
                <li><strong>Id</strong> - Record's unique identifier</li>
                <li><strong>Name</strong> - The record's name field</li>
                <li><strong>OwnerId</strong> - Record owner</li>
                <li><strong>CreatedDate</strong> - When record was created</li>
                <li><strong>SystemModstamp</strong> - Last modification timestamp</li>
                <li><strong>RecordTypeId</strong> - Record type (if applicable)</li>
                <li><strong>Master-Detail Fields</strong> - All master-detail relationships</li>
                <li><strong>Lookup Fields</strong> - All lookup relationships (with some exceptions)</li>
              </ul>
            </div>

            <div className="border-l-4 border-yellow-500 pl-4">
              <h3 className="font-semibold text-yellow-800">Sometimes Indexed:</h3>
              <ul className="text-gray-700 text-sm space-y-1 list-disc list-inside ml-2 mt-2">
                <li><strong>External ID Fields</strong> - Custom fields marked as External ID</li>
                <li><strong>Unique Fields</strong> - Custom fields marked as Unique</li>
                <li><strong>Custom Indexed Fields</strong> - Fields with custom indexes requested from Salesforce</li>
              </ul>
            </div>

            <div className="border-l-4 border-red-500 pl-4">
              <h3 className="font-semibold text-red-800">Never Indexed:</h3>
              <ul className="text-gray-700 text-sm space-y-1 list-disc list-inside ml-2 mt-2">
                <li><strong>Long Text Fields</strong> - Text Area (Long), Rich Text Area</li>
                <li><strong>Multi-Select Picklists</strong> - Cannot be indexed</li>
                <li><strong>Most Custom Text/Number Fields</strong> - Unless marked External ID or Unique</li>
                <li><strong>Formula Fields</strong> - Not indexed (with rare exceptions)</li>
              </ul>
            </div>
          </div>
        </div>

        {/* How to Identify Non-Indexed Queries */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">How to Identify Non-Indexed Queries</h2>

          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">In FlowDocs:</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Check your Performance Scorecard on the flow detail page</li>
                <li>Look for "Non-Indexed Query" in the Issues Detected section</li>
                <li>Review which Get Records elements are querying non-indexed fields</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">In Salesforce Flow Builder:</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Review your Get Records filter conditions</li>
                <li>Check if filters use custom text/number fields</li>
                <li>Look for queries filtering on fields like Description, Custom_Notes__c, etc.</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Warning Signs:</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Queries take a long time to complete (visible in debug logs)</li>
                <li>Flow timeouts in production but works fine in sandbox (due to data volume)</li>
                <li>"Unable to lock row" errors (long queries increase lock contention)</li>
              </ul>
            </div>
          </div>
        </div>

        {/* How to Fix It */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">How to Optimize Query Performance</h2>

          <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-6">
            <div className="flex">
              <LightBulbIcon className="h-6 w-6 text-green-400 mr-3 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-medium text-green-800">The Goal</h3>
                <p className="mt-2 text-sm text-green-700">
                  Ensure every Get Records element has at least one indexed field in its filter conditions.
                  Even better: use indexed fields as the primary filter, then add non-indexed fields as
                  additional filters.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Strategy 1: Add Indexed Fields to Filters</h3>
              <p className="text-gray-700 mb-3">
                If you're querying on a non-indexed field, add an indexed field to narrow the search first.
              </p>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-red-50 border border-red-200 p-3 rounded">
                  <div className="flex items-center mb-2">
                    <XCircleIcon className="h-5 w-5 text-red-500 mr-2" />
                    <h4 className="font-semibold text-red-700 text-sm">Slow</h4>
                  </div>
                  <div className="font-mono text-xs bg-white p-2 rounded">
                    Get Records: Account<br/>
                    WHERE Custom_Region__c = "West"
                  </div>
                  <p className="text-xs text-red-600 mt-2">
                    Scans all accounts to find Western region
                  </p>
                </div>

                <div className="bg-green-50 border border-green-200 p-3 rounded">
                  <div className="flex items-center mb-2">
                    <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                    <h4 className="font-semibold text-green-700 text-sm">Fast</h4>
                  </div>
                  <div className="font-mono text-xs bg-white p-2 rounded">
                    Get Records: Account<br/>
                    WHERE OwnerId = UserX<br/>
                    AND Custom_Region__c = "West"
                  </div>
                  <p className="text-xs text-green-600 mt-2">
                    Uses OwnerId index to narrow search first
                  </p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Strategy 2: Use Id or Name When Available</h3>
              <p className="text-gray-700 mb-3">
                If you have record IDs available (from parent records, trigger context, etc.), always
                query by Id first.
              </p>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-red-50 border border-red-200 p-3 rounded">
                  <div className="flex items-center mb-2">
                    <XCircleIcon className="h-5 w-5 text-red-500 mr-2" />
                    <h4 className="font-semibold text-red-700 text-sm">Slow</h4>
                  </div>
                  <div className="font-mono text-xs bg-white p-2 rounded">
                    Get Records: Contact<br/>
                    WHERE Email = currentEmail
                  </div>
                </div>

                <div className="bg-green-50 border border-green-200 p-3 rounded">
                  <div className="flex items-center mb-2">
                    <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                    <h4 className="font-semibold text-green-700 text-sm">Fast</h4>
                  </div>
                  <div className="font-mono text-xs bg-white p-2 rounded">
                    Get Records: Contact<br/>
                    WHERE Id IN collectionOfIds
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Strategy 3: Request Custom Indexes</h3>
              <p className="text-gray-700 mb-3">
                If you must query on a specific custom field frequently, you can request Salesforce
                to add a custom index to that field.
              </p>

              <div className="bg-blue-50 p-4 rounded">
                <h4 className="font-semibold text-blue-900 mb-2">How to Request a Custom Index:</h4>
                <ol className="text-blue-800 text-sm space-y-2 list-decimal list-inside ml-2">
                  <li>Open a case with Salesforce Support</li>
                  <li>Specify the object and field name (e.g., Account.Custom_Region__c)</li>
                  <li>Explain the business need and query pattern</li>
                  <li>Wait 1-3 business days for implementation</li>
                </ol>
                <p className="text-xs text-blue-700 mt-3">
                  <strong>Note:</strong> Salesforce has limits on total custom indexes per org. Use sparingly
                  for your most critical queries.
                </p>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Strategy 4: Use External ID Fields</h3>
              <p className="text-gray-700 mb-3">
                Mark frequently-queried custom fields as "External ID" in the field settings. This
                automatically creates an index.
              </p>

              <div className="bg-gray-50 p-4 rounded">
                <h4 className="font-semibold text-gray-900 mb-2">Good Candidates for External ID:</h4>
                <ul className="text-gray-700 text-sm space-y-1 list-disc list-inside ml-2">
                  <li>Integration keys (SAP_Customer_ID__c, Legacy_Account_Number__c)</li>
                  <li>Unique business identifiers (Employee_Badge_Number__c, Serial_Number__c)</li>
                  <li>Fields used in frequent lookups (Email__c on a custom object)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Real-World Example */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Real-World Example</h2>

          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded">
              <h3 className="font-semibold text-gray-900 mb-2">Scenario:</h3>
              <p className="text-gray-700 text-sm">
                Flow needs to find all Opportunities for Accounts in a specific region where
                the Close Date is in Q4 2024.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="border-2 border-red-300 rounded-lg p-4">
                <div className="flex items-center mb-3">
                  <XCircleIcon className="h-6 w-6 text-red-500 mr-2" />
                  <h3 className="font-semibold text-red-700">Slow Approach</h3>
                </div>
                <div className="space-y-2 text-sm font-mono bg-white p-3 rounded">
                  <div className="text-red-600">// First query - non-indexed</div>
                  <div>Get Records: Account</div>
                  <div>WHERE Region__c = "West"</div>
                  <div className="mt-2 text-red-600">// Loop through accounts</div>
                  <div>Loop: Accounts</div>
                  <div className="pl-4 text-red-600">Get Records: Opportunity</div>
                  <div className="pl-4">WHERE AccountId = CurrentAccount</div>
                  <div className="pl-4">AND CloseDate &gt;= 2024-10-01</div>
                  <div className="pl-4">AND CloseDate &lt;= 2024-12-31</div>
                </div>
                <div className="mt-3 space-y-1">
                  <p className="text-xs text-red-700">❌ Queries non-indexed Region field</p>
                  <p className="text-xs text-red-700">❌ Query in loop (governor limit risk)</p>
                  <p className="text-xs text-red-700">❌ Could timeout with large data</p>
                </div>
              </div>

              <div className="border-2 border-green-300 rounded-lg p-4">
                <div className="flex items-center mb-3">
                  <CheckCircleIcon className="h-6 w-6 text-green-500 mr-2" />
                  <h3 className="font-semibold text-green-700">Fast Approach</h3>
                </div>
                <div className="space-y-2 text-sm font-mono bg-white p-3 rounded">
                  <div className="text-green-600">// Single indexed query</div>
                  <div>Get Records: Opportunity</div>
                  <div>WHERE CloseDate &gt;= 2024-10-01</div>
                  <div>AND CloseDate &lt;= 2024-12-31</div>
                  <div className="mt-2 text-green-600">// Filter in loop (memory only)</div>
                  <div>Loop: Opportunities</div>
                  <div className="pl-4">Decision: Check Region</div>
                  <div className="pl-4">IF Account.Region__c = "West"</div>
                  <div className="pl-8">Add to filteredOpps</div>
                </div>
                <div className="mt-3 space-y-1">
                  <p className="text-xs text-green-700">✅ Uses indexed CloseDate field</p>
                  <p className="text-xs text-green-700">✅ One query total</p>
                  <p className="text-xs text-green-700">✅ Fast performance</p>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
              <p className="text-blue-800 text-sm">
                <strong>The Key Insight:</strong> Sometimes it's faster to query more records using an
                indexed field, then filter in memory, than to query fewer records using a non-indexed field.
              </p>
            </div>
          </div>
        </div>

        {/* Performance Impact */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Performance Impact by Data Volume</h2>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Records in Object</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Indexed Query</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Non-Indexed Query</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Difference</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-4 py-3 text-sm text-gray-900">10,000</td>
                  <td className="px-4 py-3 text-sm text-green-600">0.1s</td>
                  <td className="px-4 py-3 text-sm text-yellow-600">0.5s</td>
                  <td className="px-4 py-3 text-sm text-gray-700">5x slower</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-sm text-gray-900">100,000</td>
                  <td className="px-4 py-3 text-sm text-green-600">0.2s</td>
                  <td className="px-4 py-3 text-sm text-orange-600">3s</td>
                  <td className="px-4 py-3 text-sm text-gray-700">15x slower</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-sm text-gray-900">1,000,000</td>
                  <td className="px-4 py-3 text-sm text-green-600">0.3s</td>
                  <td className="px-4 py-3 text-sm text-red-600">20s+ (timeout)</td>
                  <td className="px-4 py-3 text-sm text-gray-700">60x+ slower</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-sm text-gray-600 mt-4">
            <em>Note: Times are approximate and vary based on query complexity, server load, and data distribution.</em>
          </p>
        </div>

        {/* Impact Summary */}
        <div className="bg-gradient-to-r from-blue-50 to-green-50 border-l-4 border-blue-400 p-6 mb-6">
          <div className="flex">
            <BoltIcon className="h-8 w-8 text-blue-600 mr-3 flex-shrink-0" />
            <div>
              <h2 className="text-xl font-bold text-blue-900 mb-2">Expected Improvement</h2>
              <div className="text-blue-800 space-y-2">
                <p className="font-semibold">Query Performance: 10-100x faster</p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>Queries complete in milliseconds instead of seconds</li>
                  <li>Eliminates timeout errors in high-volume environments</li>
                  <li>Reduces lock contention and "unable to lock row" errors</li>
                  <li>Allows flows to scale to millions of records</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Related Resources */}
        <div className="bg-white shadow-sm rounded-lg p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Related Resources</h2>
          <ul className="space-y-3">
            <li>
              <Link to={`${basePath}/help/queries-in-loops`} className="text-blue-600 hover:text-blue-800 font-medium">
                Queries in Loops →
              </Link>
              <p className="text-gray-600 text-sm mt-1">Optimizing query placement in flows</p>
            </li>
            <li>
              <Link to={`${basePath}/help/bulkification`} className="text-blue-600 hover:text-blue-800 font-medium">
                Flow Bulkification →
              </Link>
              <p className="text-gray-600 text-sm mt-1">Processing multiple records efficiently</p>
            </li>
            <li>
              <Link to={`${basePath}/help/best-practices`} className="text-blue-600 hover:text-blue-800 font-medium">
                Flow Best Practices →
              </Link>
              <p className="text-gray-600 text-sm mt-1">Complete guide to high-performance flows</p>
            </li>
          </ul>
        </div>
      </div>

      <Chatbot />
    </div>
  );
}
